// Shared Deal Analyzer projection mapping — the ONE place an analysis model
// is turned into shared-engine inputs. Both the api-server (verdict, PDF,
// chat grounding) and the dealdoor analyzer editor (displayed outputs)
// import this, so the math and displayed numbers can never drift apart.
import type {
  AnalysisOpexCategory,
  AnalysisOpexLine,
  DealAnalysisModel,
} from "./schema/dealAnalyses";
import {
  computeInvestmentProjection,
  type ProjectionResult,
} from "./investment-projections";

const finite = (v: number, fallback = 0): number =>
  Number.isFinite(v) ? v : fallback;

const OPEX_CATEGORY_BY_NORMALIZED_LABEL: Readonly<
  Record<string, AnalysisOpexCategory>
> = {
  "property taxes": "property_taxes",
  insurance: "insurance",
  utilities: "utilities",
  "repairs & maintenance": "repairs_maintenance",
  "property management": "property_management",
  "landscaping & snow": "landscaping_snow",
  "common area": "common_area",
  "reserves / capex": "reserves_capex",
  "hoa / coa dues": "hoa_coa",
  scavenger: "scavenger",
  "trash / scavenger": "scavenger",
  trash: "scavenger",
  garbage: "scavenger",
  "trash removal": "scavenger",
  other: "other",
};

export const ANALYSIS_PERCENT_OPEX_CATEGORIES: ReadonlySet<AnalysisOpexCategory> =
  new Set([
    "property_management",
    "reserves_capex",
    "repairs_maintenance",
    "other",
  ]);

export function analysisOpexCategory(
  line: Pick<AnalysisOpexLine, "category" | "label">,
): AnalysisOpexCategory {
  if (line.category) return line.category;
  return (
    OPEX_CATEGORY_BY_NORMALIZED_LABEL[line.label.trim().toLocaleLowerCase()] ??
    "other"
  );
}

export function canUseAnalysisOpexPercent(
  line: Pick<AnalysisOpexLine, "category" | "label">,
): boolean {
  return ANALYSIS_PERCENT_OPEX_CATEGORIES.has(analysisOpexCategory(line));
}

export function resolveAnalysisOpexAnnual(
  line: AnalysisOpexLine,
  grossIncome: number,
  egi?: number,
): number {
  if (line.inputMode === "percent" && canUseAnalysisOpexPercent(line)) {
    const pct =
      line.percentValue == null
        ? 0
        : Math.max(0, Math.min(100, finite(line.percentValue)));
    // Management is conventionally charged on effective gross income
    // (after vacancy); every other percentage category remains gross-based.
    const base =
      analysisOpexCategory(line) === "property_management"
        ? Math.max(0, finite(egi ?? grossIncome))
        : Math.max(0, finite(grossIncome));
    return (base * pct) / 100;
  }
  return Math.max(0, finite(line.annual));
}

export interface AnalysisYear1 {
  otherIncomeTotal: number;
  grossIncome: number;
  vacancyLoss: number;
  egi: number;
  opexTotal: number;
  noi: number;
  capitalTotal: number;
  capRate: number | null;
  expenseRatio: number | null;
}

export function impliedAnalysisPurchasePrice(
  noi: number,
  targetCapRatePct: number | null | undefined,
): number | null {
  const rate = finite(targetCapRatePct ?? 0);
  return noi > 0 && rate > 0 ? noi / (rate / 100) : null;
}

export function effectiveAnalysisPurchasePrice(
  model: DealAnalysisModel,
  _noi?: number,
): number | null {
  // Purchase price is always the active underwriting scenario. Target cap rate
  // is a valuation benchmark that can suggest an implied price, but it must not
  // silently replace the price used by returns, verdicts, exports, or AI facts.
  const price = finite(model.purchasePrice);
  return price > 0 ? price : null;
}

// Derived year-1 figures from the model — the single mapping every consumer
// (verdict, PDF export, chat grounding, client display) shares. Other income
// feeds gross BEFORE vacancy (marketplace-wide convention), capital is
// cash-funded and never touches NOI.
export function deriveAnalysisYear1(model: DealAnalysisModel): AnalysisYear1 {
  const otherIncomeTotal = model.otherIncomeLines.reduce(
    (s, l) => s + finite(l.annual),
    0,
  );
  const grossIncome = finite(model.grossRent) + otherIncomeTotal;
  const vacancyLoss = (grossIncome * finite(model.vacancyPct)) / 100;
  const egi = grossIncome - vacancyLoss;
  const opexTotal = model.opexLines.reduce(
    (s, l) => s + resolveAnalysisOpexAnnual(l, grossIncome, egi),
    0,
  );
  const noi = egi - opexTotal;
  const capitalTotal = model.capitalItems.reduce(
    (s, c) => s + (c.included && c.estCost != null ? finite(c.estCost) : 0),
    0,
  );
  const price = finite(model.purchasePrice);
  return {
    otherIncomeTotal,
    grossIncome,
    vacancyLoss,
    egi,
    opexTotal,
    noi,
    capitalTotal,
    capRate: price != null && price > 0 ? (noi / price) * 100 : null,
    expenseRatio: egi > 0 ? (opexTotal / egi) * 100 : null,
  };
}

// Run the SHARED projection engine over an analysis model. The math is never
// forked: this is the same ProjectionInput mapping the Investment Property
// Report uses, fed from the analysis's self-contained model.
export function projectAnalysisModel(
  model: DealAnalysisModel,
): ProjectionResult {
  const y1 = deriveAnalysisYear1(model);
  const s = model.projection;
  return computeInvestmentProjection({
    price: effectiveAnalysisPurchasePrice(model, y1.noi) ?? 0,
    capitalTotal: y1.capitalTotal,
    purchaseCostsPct: s.purchaseCostsPct,
    financing: s.financed
      ? {
          downPct: s.downPct,
          ratePct: s.ratePct,
          amortYears: s.amortYears,
          loanType: s.loanType ?? "conventional",
          balloonYear: s.balloonYear ?? null,
          ioYears: s.ioYears ?? null,
          refiRatePct: s.refiRatePct ?? null,
          refiAmortYears: s.refiAmortYears ?? null,
        }
      : null,
    year1: {
      grossRent: y1.grossIncome - y1.otherIncomeTotal,
      otherIncome: y1.otherIncomeTotal,
      vacancyLoss: y1.vacancyLoss,
      opexTotal: y1.opexTotal,
    },
    growth: {
      incomePctYr: s.incomeGrowthPctYr,
      expensePctYr: s.expenseGrowthPctYr,
      appreciationPctYr: s.appreciationPct,
    },
    sellingCostsPct: s.sellingCostsPct,
    depreciation: { years: s.depreciationYears, landValue: s.landValue },
    exit: {
      valuationMethod: s.valuationMethod ?? "appreciation",
      holdYears: s.holdYears ?? 30,
      exitCapAdjustmentBps: s.exitCapAdjustmentBps ?? 0,
    },
  });
}
